import { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Chip,
    Divider,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import { Add, ExpandMore } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { DosageCalculator } from './DosageCalculator';

interface DosingTableEntry {
    weight_min: number;
    weight_max: number | null;
    dose_val: number;
    dose_unit: string;
    label?: string;
}

interface ProductAmountDose {
    dose_val: number | null;
    dose_max: number | null;
    dose_unit: string | null;
    is_per_kg_bodyweight: boolean;
    dosing_table?: DosingTableEntry[];
}

interface ActiveSubstanceDose {
    substance_name: string;
    dose_val: number | null;
    dose_max: number | null;
    dose_unit: string;
    is_per_kg_bodyweight: boolean;
}

interface Scenario {
    label: string;
    species: string;
    dose_instruction: string;
    calculator_data: {
        // New schema
        product_amount_dose?: ProductAmountDose | null;
        active_substance_doses?: ActiveSubstanceDose[];
        // Shared fields
        frequency_days?: number | null;
        doses_per_day?: number | null;
        duration_days?: number | null;
        duration?: string | null;
        withdrawal_meat?: string | null;
        withdrawal_milk?: string | null;
        is_single_dose?: boolean | null;
        instructions?: string[] | null;
        label?: string;
        species?: string;
        // Legacy flat fields
        dose_val?: number | null;
        dose_min?: number | null;
        dose_max?: number | null;
        dose_unit?: string;
        is_per_kg_bodyweight?: boolean;
        dosing_table?: DosingTableEntry[];
    };
}

interface DrugDetails {
    id: string;
    name: string;
    active_substances: string[];
    scenarios: Scenario[];
    full_data: {
        display_composition?: string[];
        concentration?: string;
        pharmaceutical_form?: string;
        rcp_sections?: Record<string, string>;
        holder?: string;
        amm_number?: string;
        target_species?: string[];
        composition?: {
            substance_name: string;
            quantity: string;
            unit: string;
        }[];
    };
}

interface DrugDetailsCardProps {
    drugId: string | null;
    weight?: number | null;
    species?: string;
    onAddToPrescription: (drugId: string, scenarioLabel?: string) => void;
    isSubmitting?: boolean;
}

export function DrugDetailsCard({ drugId, weight, species, onAddToPrescription, isSubmitting = false }: DrugDetailsCardProps) {
    const { t } = useTranslation();
    const isExtensionMode = window.self !== window.top;
    const [drug, setDrug] = useState<DrugDetails | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedScenarioIndex, setSelectedScenarioIndex] = useState(0);

    useEffect(() => {
        if (!drugId) {
            setDrug(null);
            return;
        }

        const fetchDrugDetails = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await axios.get(`/api/v1/drugs/${drugId}`);
                setDrug(response.data);

                // Auto-select best scenario based on species
                if (species && response.data.scenarios) {
                    const matchingIndex = response.data.scenarios.findIndex((s: Scenario) =>
                        s.species.toLowerCase().includes(species.toLowerCase())
                    );
                    if (matchingIndex !== -1) {
                        setSelectedScenarioIndex(matchingIndex);
                    } else {
                        setSelectedScenarioIndex(0);
                    }
                } else {
                    setSelectedScenarioIndex(0);
                }
            } catch (err) {
                console.error('Failed to fetch drug details:', err);
                setError(t('errors.loadingError'));
            } finally {
                setLoading(false);
            }
        };

        fetchDrugDetails();
    }, [drugId, species, t]);

    const handleAddToPrescription = () => {
        if (!drug) return;

        const scenario = drug.scenarios[selectedScenarioIndex];
        onAddToPrescription(drug.id, scenario.label);
    };

    // Helper to format RCP section title
    const formatSectionTitle = (key: string): string => {
        // Remove numbering and clean up
        return key.replace(/^\d+\.?\s*/, '').trim();
    };

    // Helper to format RCP content with line breaks
    const formatRCPContent = (content: string): string => {
        return content
            .replace(/\\n/g, '\n')
            .trim();
    };

    if (!drugId) {
        return (
            <Box sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: isExtensionMode ? '0.8125rem' : '0.875rem' }}>
                    {t('consultation.selectDrugToView')}
                </Typography>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (error || !drug) {
        return (
            <Box sx={{ p: 2, height: '100%' }}>
                <Alert severity="error">{error || t('errors.notFound', { item: 'Drug' })}</Alert>
            </Box>
        );
    }

    const selectedScenario = drug.scenarios[selectedScenarioIndex];

    return (
        <Box sx={{ height: '100%', overflow: 'auto', pb: 2 }}>
            {/* Header - Compact */}
            <Typography variant="subtitle1" gutterBottom fontWeight="bold" sx={{ fontSize: isExtensionMode ? '0.9rem' : '1rem' }}>
                {drug.name}
            </Typography>

            {/* Active Substances - Compact */}
            {drug.active_substances.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                    {drug.active_substances.map((substance, idx) => (
                        <Chip key={idx} label={substance} size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                    ))}
                </Box>
            )}

            {/* Concentration & Form - Compact */}
            {(drug.full_data.concentration || drug.full_data.pharmaceutical_form) && (
                <Box sx={{ mb: 1 }}>
                    {drug.full_data.concentration && (
                        <Typography variant="caption" color="text.secondary" display="block">
                            <strong>Concentration:</strong> {drug.full_data.concentration}
                        </Typography>
                    )}
                    {drug.full_data.pharmaceutical_form && (
                        <Typography variant="caption" color="text.secondary" display="block">
                            <strong>Forme:</strong> {drug.full_data.pharmaceutical_form}
                        </Typography>
                    )}
                </Box>
            )}

            <Divider sx={{ my: 1 }} />

            {/* Scenario Selector - Compact */}
            {drug.scenarios.length > 1 && (
                <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                    <InputLabel sx={{ fontSize: '0.875rem' }}>Posologie</InputLabel>
                    <Select
                        value={selectedScenarioIndex}
                        onChange={(e) => setSelectedScenarioIndex(Number(e.target.value))}
                        label="Posologie"
                        sx={{ fontSize: '0.875rem' }}
                    >
                        {drug.scenarios.map((scenario, idx) => (
                            <MenuItem key={idx} value={idx} sx={{ fontSize: '0.875rem' }}>
                                {scenario.species} - {scenario.label || 'Standard'}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            {/* Selected Scenario Info - Compact */}


            {/* Dosage Calculator */}
            {selectedScenario && (
                <Box sx={{ mb: 1 }}>
                    <DosageCalculator
                        weight={weight}
                        drugName={drug.name}
                        pharmaceuticalForm={drug.full_data.pharmaceutical_form}
                        scenario={selectedScenario.calculator_data}
                        composition={drug.full_data.composition}
                    />
                </Box>
            )}

            <Divider sx={{ my: 1 }} />

            {/* Complete RCP Information - All Sections */}
            {drug.full_data.rcp_sections && Object.keys(drug.full_data.rcp_sections).length > 0 && (
                <Box sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ fontSize: '0.9rem' }}>
                        RCP Complet
                    </Typography>

                    {Object.entries(drug.full_data.rcp_sections).map(([key, value]) => {
                        const title = formatSectionTitle(key);
                        const content = formatRCPContent(value);

                        // Highlight important sections
                        const isWarning = key.toLowerCase().includes('attente') || key.toLowerCase().includes('withdrawal');
                        const isDanger = key.toLowerCase().includes('contre') || key.toLowerCase().includes('contraindication');

                        return (
                            <Accordion key={key} defaultExpanded={isWarning}>
                                <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                                    <Typography
                                        variant="caption"
                                        fontWeight="bold"
                                        color={isWarning ? 'warning.main' : isDanger ? 'error.main' : 'text.primary'}
                                        sx={{ fontSize: '0.8rem' }}
                                    >
                                        {title}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                                    <Typography
                                        variant="body2"
                                        component="div"
                                        sx={{
                                            whiteSpace: 'pre-wrap',
                                            fontSize: isExtensionMode ? '0.7rem' : '0.75rem',
                                            color: isDanger ? 'error.dark' : isWarning ? 'warning.dark' : 'text.primary',
                                            '& p': {
                                                margin: '0.5em 0',
                                            },
                                            '& ul, & ol': {
                                                paddingLeft: '1.5em',
                                                margin: '0.5em 0',
                                            },
                                        }}
                                        dangerouslySetInnerHTML={{ __html: content }}
                                    />
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </Box>
            )}

            {/* Metadata - Compact */}
            {(drug.full_data.holder || drug.full_data.amm_number) && (
                <Box sx={{ mb: 1, p: 0.5, bgcolor: 'background.default', borderRadius: 1 }}>
                    {drug.full_data.holder && (
                        <Typography variant="caption" color="text.secondary" display="block" fontSize="0.7rem">
                            Titulaire: {drug.full_data.holder}
                        </Typography>
                    )}
                    {drug.full_data.amm_number && (
                        <Typography variant="caption" color="text.secondary" display="block" fontSize="0.7rem">
                            AMM: {drug.full_data.amm_number}
                        </Typography>
                    )}
                </Box>
            )}

            {/* Add to Prescription Button - Sticky */}
            <Box sx={{ pt: 1, position: 'sticky', bottom: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                <Button
                    fullWidth
                    variant="contained"
                    startIcon={!isSubmitting && <Add />}
                    onClick={handleAddToPrescription}
                    size="small"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? <CircularProgress size={24} color="inherit" /> : t('consultation.addToPrescription')}
                </Button>
            </Box>
        </Box>
    );
}
